import { html, css, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import "../components/settings-profile.js";
import "../components/settings-security.js";
import "../components/settings-tokens.js";

const SECTIONS = [
  { key: "profile", label: "Profile" },
  { key: "security", label: "Security" },
  { key: "tokens", label: "Tokens" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

/**
 * page-settings is the authenticated account area: a small sub-nav (Profile,
 * Security) beside the active section. Unknown sections fall back to Profile.
 */
@customElement("page-settings")
export class PageSettings extends PageElement {
  static override styles = [
    shared,
    css`
      .layout {
        display: grid;
        grid-template-columns: 180px 1fr;
        gap: var(--sp-4);
        align-items: start;
      }
      nav {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .tab {
        font: 500 13px/1 var(--sans);
        color: var(--text-dim);
        padding: 8px 11px;
        border-radius: 7px;
        text-decoration: none;
      }
      .tab:hover {
        color: var(--text);
        text-decoration: none;
      }
      .tab.active {
        color: var(--text);
        font-weight: 600;
        background: var(--surface-3);
      }
      @media (max-width: 720px) {
        .layout {
          grid-template-columns: 1fr;
        }
        nav {
          flex-direction: row;
        }
      }
    `,
  ];

  @property() section = "profile";

  private get active(): SectionKey {
    return SECTIONS.some((s) => s.key === this.section) ? (this.section as SectionKey) : "profile";
  }

  override willUpdate(changed: PropertyValues) {
    if (changed.has("section")) {
      const label = SECTIONS.find((s) => s.key === this.active)?.label ?? "Profile";
      this.setTitle(`Settings · ${label}`);
    }
  }

  override render() {
    return html`
      <div class="wrap-narrow">
        <h1>Settings</h1>
        <div class="layout">
          <nav aria-label="Settings">
            ${SECTIONS.map(
              (s) => html`<a class="tab ${this.active === s.key ? "active" : ""}" href="/settings/${s.key}"
                >${s.label}</a
              >`,
            )}
          </nav>
          <div>
            ${this.active === "security"
              ? html`<settings-security></settings-security>`
              : this.active === "tokens"
                ? html`<settings-tokens></settings-tokens>`
                : html`<settings-profile></settings-profile>`}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-settings": PageSettings;
  }
}
